import { useState, useEffect } from "react";
import { useNavigate, useLocation, Navigate } from "react-router-dom";
import { Lock, User, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import authapi from "@/services/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Card, CardContent } from "@/components/ui/card";

const LoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isLogged, login } = useAuth();

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Hata mesajını input değiştiğinde temizle
  useEffect(() => {
    setError(null);
  }, [username, password]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    try {
      const { data: res } = await authapi.post("/Auth/Token", {
        uniqueValue: username.trim(),
        password,
      });

      if (res.isTwoFactorEnabled) {
        // 2FA aktif — doğrulama sayfasına yönlendir
        navigate("/two-factor", {
          state: {
            userId: res.data?.UserId,
            from: location.state?.from,
          },
          replace: true,
        });
        return;
      }

      // 2FA yok — direkt giriş
      const token = res.data?.Token;
      if (!token) {
        setError("Sunucudan geçerli bir token alınamadı.");
        return;
      }

      try {
        login(token);
      } catch (err) {
        if (err.message === "UNAUTHORIZED_ROLE") {
          setError("Geçersiz işlem denemesi. Bu sisteme erişim yetkiniz bulunmamaktadır.");
          return;
        }
        throw err;
      }
    } catch (err) {
      // authapi interceptor zaten toast gösteriyor, burada sadece form'a yansıt
      if (!error) {
        const msg =
          err?.response?.data?.message ||
          err?.response?.data?.Message ||
          err?.response?.data?.ExceptionMessage ||
          "Giriş yapılamadı. Lütfen bilgilerinizi kontrol edin.";
        setError(msg);
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (isLogged) {
    const dest = location.state?.from?.pathname || "/";
    return <Navigate to={dest} replace />;
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4 py-10">
      <div className="w-full max-w-md">
        {/* Brand */}
        <div className="flex items-center justify-center gap-2 mb-8">
          <div className="w-10 h-10 rounded-md bg-slate-900 text-white flex items-center justify-center font-black">
            A
          </div>
          <span className="font-heading font-black text-2xl tracking-tighter text-slate-900">
            ALTINGROSS
          </span>
        </div>

        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-8">
            <div className="space-y-1 mb-6">
              <h2
                className="font-heading text-2xl font-bold tracking-tight text-slate-900"
                data-testid="login-title"
              >
                Giriş Yap
              </h2>
              <p className="text-sm text-slate-500">
                Devam etmek için hesabınıza giriş yapın.
              </p>
            </div>

            <form
              onSubmit={handleSubmit}
              className="space-y-5"
              data-testid="login-form"
            >
              <div className="space-y-2">
                <Label
                  htmlFor="username"
                  className="text-sm font-medium text-slate-700"
                >
                  Kullanıcı Adı / E-posta
                </Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="username"
                    type="text"
                    autoComplete="username"
                    required
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    placeholder="Kullanıcı adı veya e-posta"
                    className="pl-9 h-11"
                    data-testid="login-username-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label
                  htmlFor="password"
                  className="text-sm font-medium text-slate-700"
                >
                  Şifre
                </Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="password"
                    type="password"
                    autoComplete="current-password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className="pl-9 h-11"
                    data-testid="login-password-input"
                  />
                </div>
              </div>

              {error && (
                <Alert variant="destructive" data-testid="login-error">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <Button
                type="submit"
                disabled={submitting || !username || !password}
                className="w-full h-11 bg-blue-600 hover:bg-blue-700 text-white font-medium"
                data-testid="login-submit-button"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Giriş yapılıyor...
                  </>
                ) : (
                  "Giriş Yap"
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        <p className="text-xs text-center text-slate-400 mt-6">
          Bu uygulama sadece ALTINGROSS çalışanları içindir.
        </p>
      </div>
    </div>
  );
};

export default LoginPage;
